import { Icon } from "@/common/Icon";
import { useState, useEffect } from "react";
import { useLocale } from "@/store/locale-store";
import { useShippingCountries } from "@/hooks/shops/useCountries";
import { getDefaultLanguageCode } from "@/utils/LocaleUtil";

const DISMISS_STORAGE_KEY = "dismissSuggestion";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// 지오기반으로 국가 제안을 하므로
// 해당국가의 디폴트 언어로 번역문을 제공
const TRANSLATIONS = {
  en: {
    suggestLocaleMessage:
      "Would you like to set your country or region to {countryName} for service?",
    yes: "Yes",
    no: "No",
  },
  ko: {
    suggestLocaleMessage:
      "서비스를 위해 귀하의 국가 또는 지역을 {countryName}(으)로 설정하시겠습니까?",
    yes: "예",
    no: "아니요",
  },
  ja: {
    suggestLocaleMessage:
      "サービスのためにあなたの国または地域を{countryName}に設定しますか？",
    yes: "はい",
    no: "いいえ",
  },
  zh: {
    suggestLocaleMessage: "您想将您的国家或地区设置为{countryName}吗？",
    yes: "是",
    no: "否",
  },
  fr: {
    suggestLocaleMessage:
      "Souhaitez-vous définir votre pays ou région sur {countryName} pour le service ?",
    yes: "Oui",
    no: "Non",
  },
  de: {
    suggestLocaleMessage:
      "Möchten Sie Ihr Land oder Ihre Region für den Service auf {countryName} einstellen?",
    yes: "Ja",
    no: "Nein",
  },
  es: {
    suggestLocaleMessage:
      "¿Le gustaría configurar su país o región en {countryName} para el servicio?",
    yes: "Sí",
    no: "No",
  },
};

const Layout = ({ children }: { [key: string]: any }) => {
  return (
    <div className="w-full bg-[#282828] px-5 py-5 smd:py-4">
      <div className="mx-auto max-w-screen-xl">{children}</div>
    </div>
  );
};

export default function SuggestLocale({
  countryCode,
}: {
  countryCode: string;
}) {
  const { data, isPending } = useShippingCountries();
  const allCountries = data ? Object.values(data).flat() : [];
  const countryName =
    allCountries.find((c) => c.countryCode === countryCode)?.ownCountryName ||
    countryCode;
  const [visible, setVisible] = useState<boolean>(false);
  const { setLocale, setDialog } = useLocale();
  const defaultLanguage = getDefaultLanguageCode(countryCode);

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < DISMISS_DURATION_MS) {
        setVisible(false);
        return; // 24시간 이내에 dismiss한 경우 표시하지 않음
      }
      localStorage.removeItem(DISMISS_STORAGE_KEY); // 만료된 경우 삭제
    }

    setVisible(true);
  }, [countryCode]);

  if (!visible || isPending) {
    return null;
  }

  return (
    <>
      <Layout>
        <div className="flex items-center justify-center text-xs font-500 text-white">
          <div className="flex flex-wrap items-center justify-start gap-x-2.5 gap-y-5">
            <p>
              {TRANSLATIONS[defaultLanguage]?.suggestLocaleMessage.replace(
                "{countryName}",
                countryName,
              ) ||
                `Would you like to set your country or region to ${countryName} for service?`}
            </p>
            <div className="hidden items-center gap-x-1.5 whitespace-nowrap rounded-md laptop:flex">
              <Icon name="earth" />
            </div>
            <div className="flex grow flex-nowrap justify-end gap-x-10">
              <div className="grid auto-cols-fr grid-flow-col gap-x-[6px] leading-none">
                <button
                  className="min-w-[80px] rounded bg-[#f3f3f3] px-1 py-3 text-black"
                  onClick={() => {
                    const languageCode = getDefaultLanguageCode(countryCode);
                    setLocale({
                      countryCode,
                      languageCode,
                    });
                  }}
                >
                  {TRANSLATIONS[defaultLanguage]?.yes || "Yes"}
                </button>
                <button
                  className="rounded bg-[#5a5a5a] px-1 py-3"
                  onClick={() => {
                    localStorage.setItem(
                      DISMISS_STORAGE_KEY,
                      Date.now().toString(),
                    );
                    setDialog("select-geo-based-locale-dialog");
                    setVisible(false);
                  }}
                >
                  {TRANSLATIONS[defaultLanguage]?.no || "No"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}
